import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CalendarRange } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type CurrentUser, type DepartmentRef, type Paginated, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { toDateTimeLocal } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Button, Checkbox, Field, Input, Select, Textarea, useToast } from '../components/ui';
import { AttachmentsField, useUploads } from '../components/Attachments';
import { NameField, hasDescription, nameFits } from '../components/NameField';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

/**
 * The departments a person may add a Q Task to — the server's rule: with
 * `q_tasks.create`, which only administrators hold since 2026-10-07, one they
 * lead or, with the override, any department whose queue they can see.
 * `'all'` is every department.
 */
export function creatableQTaskDepartments(
  user: CurrentUser | null,
  can: (...permissions: string[]) => boolean,
): 'all' | DepartmentRef[] {
  if (!user || !can(P.Q_TASKS_CREATE)) return [];
  if (can(P.Q_TASKS_OVERRIDE)) return can(P.Q_TASKS_VIEW_ALL) ? 'all' : user.departments;
  return user.departments.filter((d) => d.memberType === 'LEADER');
}

/** Route: `/q-tasks/new`, optionally `?departmentId=…`. */
export function QTaskFormPage() {
  const { t, locale, localized } = useI18n();
  const { user, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState({
    departmentId: searchParams.get('departmentId') ?? '',
    title: '',
    description: '',
    priority: 'MEDIUM',
  });
  const uploads = useUploads();

  const creatable = creatableQTaskDepartments(user, can);
  const { data: allDepartments } = useQuery({
    queryKey: ['departments', 'options'],
    queryFn: () => api.get<Paginated<DepartmentRef>>('/departments', { pageSize: 100 }),
    enabled: creatable === 'all',
  });
  const departments = creatable === 'all' ? allDepartments?.items ?? [] : creatable;
  // With one department to choose from, it is chosen.
  const departmentId = form.departmentId || (departments.length === 1 ? departments[0].id : '');

  const create = useMutation({
    mutationFn: () =>
      api.post('/q-tasks', {
        departmentId,
        title: form.title,
        description: form.description,
        priority: form.priority,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['q-tasks'] });
      navigate('/q-tasks');
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('qtasks.new')}
      subtitle={t('qtasks.queueTailNote')}
      backTo="/q-tasks"
      backLabel={t('nav.qTasks')}
      submitLabel={t('common.create')}
      submitting={create.isPending}
      // Saving while a file is still uploading would leave it behind.
      disabled={!nameFits(form.title) || !hasDescription(form.description) || !departmentId || uploads.busy}
      onSubmit={() => create.mutate()}
    >
      <div className="space-y-4">
        {!can(P.Q_TASKS_CREATE) && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {t('qtasks.createAdminsOnly')}
          </p>
        )}
        <Field label={t('common.department')} required>
          <Select value={departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })}>
            <option value="">—</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{localized(d)}</option>
            ))}
          </Select>
        </Field>
        <NameField label={t('common.name')} value={form.title} onChange={(title) => setForm({ ...form, title })} autoFocus />
        <Field label={t('common.description')} required>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label={t('common.priority')}>
          <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{enumLabel(p, locale)}</option>
            ))}
          </Select>
        </Field>
        <AttachmentsField uploads={uploads} />
      </div>
    </FormPage>
  );
}

type QTaskEditForm = { title: string; description: string; priority: string };

/**
 * Route: `/q-tasks/:id/edit`. A Q Task can be edited until someone starts on it
 * — while it is in the pool, or with someone who has not begun — by whoever
 * created it or a leader of its department. It stays in its department's queue;
 * who has it and its dates change with Assign, and its files are added on its
 * page, so the form has none of them.
 */
export function QTaskEditPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existing = useQuery({ queryKey: ['q-task', id], queryFn: () => api.get<any>(`/q-tasks/${id}`) });
  const qTask = existing.data;
  const [form, setForm] = useState<QTaskEditForm>({ title: '', description: '', priority: 'MEDIUM' });
  // The Q Task as it was when the form opened: Save waits for a change.
  const [initial, setInitial] = useState<QTaskEditForm | null>(null);
  if (qTask && !initial) {
    const loaded = { title: qTask.title, description: qTask.description ?? '', priority: qTask.priority ?? 'MEDIUM' };
    setInitial(loaded);
    setForm(loaded);
  }
  const locked = !!qTask && !qTask.can?.edit;
  const dirty = !!initial && (Object.keys(form) as Array<keyof QTaskEditForm>).some((key) => form[key] !== initial[key]);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/q-tasks/${id}`, { title: form.title, description: form.description, priority: form.priority }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['q-tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['q-task', id] });
      toast.success(t('qtasks.saved'));
      // In place of the form, so going back from the Q Task does not reopen it.
      navigate(`/q-tasks/${id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('qtasks.edit')}
      subtitle={
        qTask
          ? locked
            ? `${qTask.number} — ${qTask.title}`
            : `${qTask.number} — ${t('qtasks.editableUntilStarted')}`
          : undefined
      }
      backTo={`/q-tasks/${id}`}
      backLabel={qTask?.number ?? t('nav.qTasks')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={locked || !dirty || !nameFits(form.title) || !hasDescription(form.description)}
      loading={existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <div className="space-y-4">
        {locked && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {['QUEUED', 'AVAILABLE', 'ASSIGNED'].includes(qTask.status) ? t('qtasks.editNotYours') : t('qtasks.startedLocked')}
          </p>
        )}
        <NameField
          label={t('common.name')}
          value={form.title}
          onChange={(title) => setForm({ ...form, title })}
          disabled={locked}
          autoFocus
        />
        <Field label={t('common.description')} required>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            disabled={locked}
          />
        </Field>
        <FormGrid>
          <Field label={t('common.priority')}>
            <Select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              disabled={locked}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{enumLabel(p, locale)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.department')} hint={t('qtasks.departmentFixed')}>
            <Input value={qTask ? localized(qTask.department) : ''} disabled readOnly />
          </Field>
          {/* Who has it, and when, are a leader's to change, with Assign. */}
          <Field label={t('common.assignee')} hint={t('qtasks.assigneeByAssign')}>
            <Input
              value={qTask ? (qTask.assignee ? localized(qTask.assignee) : t('common.unassigned')) : ''}
              disabled
              readOnly
            />
          </Field>
        </FormGrid>
      </div>
    </FormPage>
  );
}

/**
 * Blocking, skipping and cancelling a Q Task all take a reason (§21), so each
 * is a page. Route: `/q-tasks/:id/:action` where action is block|skip|cancel.
 */
export function QTaskActionPage() {
  const { id = '', action = 'block' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const labels: Record<string, string> = {
    block: t('qtasks.block'),
    skip: t('qtasks.skip'),
    cancel: t('common.cancel'),
  };

  const qTask = useQuery({
    queryKey: ['q-task', id],
    queryFn: () => api.get<any>(`/q-tasks/${id}`),
  });

  const run = useMutation({
    mutationFn: () => api.post(`/q-tasks/${id}/${action}`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['q-tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['q-task', id] });
      navigate('/q-tasks');
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={labels[action] ?? action}
      subtitle={qTask.data ? `${qTask.data.number} — ${qTask.data.title}` : undefined}
      backTo="/q-tasks"
      backLabel={t('nav.qTasks')}
      submitLabel={t('common.confirm')}
      destructive={action === 'cancel'}
      submitting={run.isPending}
      disabled={!reason.trim()}
      loading={qTask.isLoading}
      error={qTask.error}
      onRetry={() => void qTask.refetch()}
      onSubmit={() => run.mutate()}
    >
      <Field label={t('common.reason')} required>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </FormPage>
  );
}

/** The Q Task, and the department's people a leader can give it — or its tasks — to. */
function useQTaskWithWorkers(id: string) {
  const qTask = useQuery({ queryKey: ['q-task', id], queryFn: () => api.get<any>(`/q-tasks/${id}`) });
  const workers = useQuery({
    queryKey: ['q-task', id, 'candidates'],
    queryFn: () => api.get<UserRef[]>(`/q-tasks/${id}/candidates`),
  });
  return {
    qTask: qTask.data,
    workers: workers.data ?? [],
    loading: qTask.isLoading || workers.isLoading,
    error: qTask.error ?? workers.error,
    retry: () => {
      void qTask.refetch();
      void workers.refetch();
    },
  };
}

/**
 * Route: `/q-tasks/:id/assign` — a leader gives a Q Task to someone who works
 * the department's Q Tasks, with a start and a deadline, or moves it to someone
 * else. Keeping whoever has it changes only the dates.
 */
export function QTaskAssignPage() {
  const { id = '' } = useParams();
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Each field shows what the Q Task has now until it is changed.
  const [picked, setPicked] = useState<string | null>(null);
  const [dates, setDates] = useState<{ startAt: string; dueAt: string } | null>(null);
  const [note, setNote] = useState('');
  const { qTask, workers, loading, error, retry } = useQTaskWithWorkers(id);

  const current = qTask?.assignee;
  const assigneeUserId = picked ?? qTask?.assignedUserId ?? '';
  const saved = { startAt: toDateTimeLocal(qTask?.startAt), dueAt: toDateTimeLocal(qTask?.dueAt) };
  const { startAt, dueAt } = dates ?? saved;
  const backwards = !!startAt && !!dueAt && new Date(dueAt) <= new Date(startAt);
  const rescheduling = !!current && assigneeUserId === current.id;
  // Its own person and its own dates again would change nothing.
  const unchanged = rescheduling && startAt === saved.startAt && dueAt === saved.dueAt;

  const chosen = workers.find((w) => w.id === assigneeUserId);
  const assign = useMutation({
    mutationFn: () =>
      api.post(`/q-tasks/${id}/assign`, {
        assigneeUserId,
        // datetime-local is the browser's own clock; the API wants an instant.
        startAt: new Date(startAt).toISOString(),
        dueAt: new Date(dueAt).toISOString(),
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['q-tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['q-task', id] });
      toast.success(
        rescheduling ? t('qtasks.rescheduled') : t('qtasks.assignedTo', { name: chosen ? localized(chosen) : '' }),
      );
      navigate(`/q-tasks/${id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('qtasks.assignTitle')}
      subtitle={qTask ? `${qTask.number} — ${qTask.title}` : undefined}
      backTo={`/q-tasks/${id}`}
      backLabel={qTask?.number ?? t('nav.qTasks')}
      submitLabel={rescheduling ? t('common.save') : t('qtasks.assign')}
      submitting={assign.isPending}
      disabled={!assigneeUserId || !startAt || !dueAt || backwards || unchanged}
      loading={loading}
      error={error}
      onRetry={retry}
      onSubmit={() => assign.mutate()}
    >
      <div className="space-y-4">
        <Field
          label={t('qtasks.assignTo')}
          required
          hint={
            workers.length === 0
              ? t('qtasks.noWorkers')
              : current
                ? t('qtasks.nowWith', { name: localized(current) })
                : t('qtasks.assignHint')
          }
        >
          <Select value={assigneeUserId} onChange={(e) => setPicked(e.target.value)} autoFocus>
            <option value="">—</option>
            {workers.map((w) => (
              <option key={w.id} value={w.id}>
                {localized(w)}
              </option>
            ))}
          </Select>
        </Field>
        <FormGrid>
          <Field label={t('tasks.startDate')} required>
            <Input
              type="datetime-local"
              value={startAt}
              onChange={(e) => setDates({ startAt: e.target.value, dueAt })}
            />
          </Field>
          <Field label={t('tasks.deadline')} required error={backwards ? t('tasks.dueAfterStart') : null}>
            <Input
              type="datetime-local"
              value={dueAt}
              min={startAt || undefined}
              onChange={(e) => setDates({ startAt, dueAt: e.target.value })}
            />
          </Field>
        </FormGrid>
        <Field label={t('common.notes')} hint={t('common.optional')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </FormPage>
  );
}

interface ConvertRow {
  checked: boolean;
  title: string;
  startAt: string;
  dueAt: string;
}

const BLANK_ROW: ConvertRow = { checked: false, title: '', startAt: '', dueAt: '' };

/** What stops a row: a date left out, or a deadline that is not after the start. */
function rowProblem(row: ConvertRow): 'missing' | 'order' | null {
  if (!row.startAt || !row.dueAt) return 'missing';
  return new Date(row.dueAt) > new Date(row.startAt) ? null : 'order';
}

/**
 * Route: `/q-tasks/:id/convert` — a leader turns a Q Task into tasks. Everyone
 * ticked gets a task of their own, with its own title, start and deadline; the
 * Q Task then closes as converted. One person ticked is "convert to a task".
 */
export function QTaskConvertPage() {
  const { id = '' } = useParams();
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Record<string, ConvertRow>>({});
  const { qTask, workers, loading, error, retry } = useQTaskWithWorkers(id);

  const rowOf = (userId: string) => rows[userId] ?? BLANK_ROW;
  const change = (userId: string, patch: Partial<ConvertRow>) =>
    setRows((all) => ({ ...all, [userId]: { ...(all[userId] ?? BLANK_ROW), ...patch } }));
  // Ticking someone starts their task with the Q Task's title, ready to change.
  const tick = (userIds: string[], checked: boolean) =>
    setRows((all) => {
      const next = { ...all };
      for (const userId of userIds) {
        const row = next[userId] ?? BLANK_ROW;
        next[userId] = { ...row, checked, title: row.title || qTask?.title || '' };
      }
      return next;
    });
  const shareDates = (from: ConvertRow) =>
    setRows((all) =>
      Object.fromEntries(
        Object.entries(all).map(([userId, row]) => [
          userId,
          row.checked ? { ...row, startAt: from.startAt, dueAt: from.dueAt } : row,
        ]),
      ),
    );

  const chosen = workers.filter((w) => rowOf(w.id).checked);
  // Each task needs a name of up to 25 characters: a Q Task from before the limit may have a longer one.
  const ready =
    chosen.length > 0 && chosen.every((w) => rowProblem(rowOf(w.id)) === null && nameFits(rowOf(w.id).title));

  const convert = useMutation({
    mutationFn: () =>
      api.post(`/q-tasks/${id}/convert`, {
        tasks: chosen.map((w) => {
          const row = rowOf(w.id);
          return {
            assigneeUserId: w.id,
            title: row.title.trim(),
            startAt: new Date(row.startAt).toISOString(),
            dueAt: new Date(row.dueAt).toISOString(),
          };
        }),
      }),
    onSuccess: () => {
      for (const queryKey of [['q-tasks'], ['q-task', id], ['kanban'], ['tasks']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      toast.success(t('qtasks.converted'));
      navigate(`/q-tasks/${id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('qtasks.convert')}
      subtitle={qTask ? `${qTask.number} — ${qTask.title}` : undefined}
      backTo={`/q-tasks/${id}`}
      backLabel={qTask?.number ?? t('nav.qTasks')}
      submitLabel={t('qtasks.convert')}
      submitting={convert.isPending}
      disabled={!ready}
      loading={loading}
      error={error}
      onRetry={retry}
      onSubmit={() => convert.mutate()}
    >
      <p className="mb-5 text-body-sm text-muted">{t('qtasks.convertHint')}</p>
      {workers.length === 0 ? (
        <p className="text-body-sm text-muted">{t('qtasks.noWorkers')}</p>
      ) : (
        <fieldset>
          <legend className="mb-2 text-label text-[var(--text-secondary)]">{t('qtasks.people')}</legend>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <Checkbox
              label={t('qtasks.everyone')}
              checked={chosen.length === workers.length}
              onChange={(e) => tick(workers.map((w) => w.id), e.target.checked)}
            />
            <span className="tabular text-caption text-muted">{t('qtasks.selectedCount', { n: chosen.length })}</span>
          </div>
          <ul className="space-y-3">
            {workers.map((w) => {
              const row = rowOf(w.id);
              const problem = row.checked ? rowProblem(row) : null;
              return (
                <li
                  key={w.id}
                  className={clsx(
                    'rounded-md border hairline p-3 transition-colors duration-fast',
                    row.checked && 'border-primary/50',
                  )}
                >
                  <Checkbox label={localized(w)} checked={row.checked} onChange={(e) => tick([w.id], e.target.checked)} />
                  {row.checked && (
                    <div className="mt-3 space-y-4">
                      <NameField
                        label={t('qtasks.taskTitle')}
                        value={row.title}
                        placeholder={qTask?.title}
                        onChange={(title) => change(w.id, { title })}
                      />
                      <FormGrid>
                        <Field label={t('tasks.startDate')} required>
                          <Input
                            type="datetime-local"
                            value={row.startAt}
                            onChange={(e) => change(w.id, { startAt: e.target.value })}
                          />
                        </Field>
                        <Field
                          label={t('tasks.deadline')}
                          required
                          error={problem === 'order' ? t('tasks.dueAfterStart') : null}
                        >
                          <Input
                            type="datetime-local"
                            value={row.dueAt}
                            min={row.startAt || undefined}
                            onChange={(e) => change(w.id, { dueAt: e.target.value })}
                          />
                        </Field>
                      </FormGrid>
                      {chosen.length > 1 && problem === null && (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<CalendarRange className="h-4 w-4" />}
                          onClick={() => shareDates(row)}
                        >
                          {t('qtasks.sameDates')}
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </fieldset>
      )}
    </FormPage>
  );
}
